/**
 * Central-directory-only ZIP reader.
 *
 * §21: "Do not fully extract an archive merely to display its file list."
 * Every ZIP-based format we support — DOCX, XLSX, PPTX, HWPX, EPUB, ODF,
 * Sketch, iWork — is identified by its entry layout, and archive browsing shows
 * the same listing. Both needs are served by parsing the central directory and
 * nothing else, which is O(entries) and never inflates a byte.
 *
 * The limits enforced here are the §21 archive-bomb defences. They apply at
 * *listing* time, before any decompression is even offered.
 */

export interface ZipEntry {
  readonly name: string;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  /** 0 = stored, 8 = deflate; others are unsupported for extraction. */
  readonly method: number;
  readonly localHeaderOffset: number;
  /** Bit 0 of the general-purpose flags. */
  readonly encrypted: boolean;
  /** Entry paths that escape the extraction root, per §21 Zip Slip defence. */
  readonly unsafePath: boolean;
}

export interface ZipDirectory {
  readonly entries: readonly ZipEntry[];
  readonly totalUncompressed: number;
  /** Highest single-entry compression ratio seen. */
  readonly maxRatio: number;
  readonly zip64: boolean;
  readonly violations: readonly ZipViolation[];
}

export type ZipViolation =
  | { readonly kind: 'too-many-entries'; readonly count: number; readonly limit: number }
  | { readonly kind: 'expansion-too-large'; readonly bytes: number; readonly limit: number }
  | { readonly kind: 'ratio-too-high'; readonly entry: string; readonly ratio: number; readonly limit: number }
  | { readonly kind: 'unsafe-path'; readonly entry: string }
  | { readonly kind: 'encrypted'; readonly entry: string }
  | { readonly kind: 'zip64-unsupported' };

export interface ZipLimits {
  readonly maxEntries: number;
  readonly maxTotalUncompressed: number;
  readonly maxRatio: number;
}

/** §21 defaults. Callers may raise them for explicitly trusted local files. */
export const DEFAULT_ZIP_LIMITS: ZipLimits = {
  maxEntries: 10_000,
  maxTotalUncompressed: 2 * 1024 * 1024 * 1024, // 2 GiB
  maxRatio: 200,
};

const EOCD_SIG = 0x06054b50;
const CD_SIG = 0x02014b50;
const EOCD_MAX_COMMENT = 0xffff;

export class ZipFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipFormatError';
  }
}

/**
 * A ZIP entry name is unsafe when extracting it could write outside the chosen
 * destination: absolute paths, drive letters, UNC paths, or any `..` segment.
 * Backslashes are normalised first because Windows-produced archives use them
 * even though the spec mandates forward slashes.
 */
export function isUnsafeEntryPath(name: string): boolean {
  const n = name.replace(/\\/g, '/');
  if (n.startsWith('/')) return true;
  if (/^[a-zA-Z]:/.test(n)) return true;
  if (n.startsWith('//')) return true; // UNC
  return n.split('/').includes('..');
}

/** Locate the end-of-central-directory record by scanning backwards. */
function findEocd(buf: Uint8Array): number {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const minPos = Math.max(0, buf.length - EOCD_MAX_COMMENT - 22);
  for (let i = buf.length - 22; i >= minPos; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) return i;
  }
  return -1;
}

/**
 * Parse the central directory of a complete ZIP buffer.
 *
 * Only the tail of the file is strictly needed, but callers that already hold
 * the whole buffer (the common case for the documents we open) pass it through
 * unchanged — nothing is copied.
 */
export function readZipDirectory(
  buf: Uint8Array,
  limits: ZipLimits = DEFAULT_ZIP_LIMITS,
): ZipDirectory {
  if (buf.length < 22) throw new ZipFormatError('file is too short to contain a ZIP end record');

  const eocdPos = findEocd(buf);
  if (eocdPos < 0) throw new ZipFormatError('end-of-central-directory record not found');

  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const violations: ZipViolation[] = [];

  const entryCount = view.getUint16(eocdPos + 10, true);
  const cdSize = view.getUint32(eocdPos + 12, true);
  const cdOffset = view.getUint32(eocdPos + 16, true);

  // ZIP64 sentinels. We detect and report rather than silently mis-parse.
  const zip64 = entryCount === 0xffff || cdSize === 0xffffffff || cdOffset === 0xffffffff;
  if (zip64) violations.push({ kind: 'zip64-unsupported' });

  if (entryCount > limits.maxEntries) {
    violations.push({ kind: 'too-many-entries', count: entryCount, limit: limits.maxEntries });
  }

  const entries: ZipEntry[] = [];
  let pos = cdOffset;
  let totalUncompressed = 0;
  let maxRatio = 0;
  const readLimit = Math.min(entryCount, limits.maxEntries);

  for (let i = 0; i < readLimit; i++) {
    if (pos + 46 > buf.length) throw new ZipFormatError(`central directory truncated at entry ${i}`);
    if (view.getUint32(pos, true) !== CD_SIG) {
      throw new ZipFormatError(`bad central directory signature at entry ${i}`);
    }

    const flags = view.getUint16(pos + 8, true);
    const method = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const uncompressedSize = view.getUint32(pos + 24, true);
    const nameLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    const localHeaderOffset = view.getUint32(pos + 42, true);

    const nameBytes = buf.subarray(pos + 46, pos + 46 + nameLen);
    // Bit 11 signals UTF-8. Archives that omit it are usually UTF-8 anyway in
    // practice, and a non-fatal decode keeps multilingual names readable (§25).
    const name = new TextDecoder('utf-8').decode(nameBytes);

    const encrypted = (flags & 0x1) !== 0;
    if (encrypted) violations.push({ kind: 'encrypted', entry: name });

    const unsafePath = isUnsafeEntryPath(name);
    if (unsafePath) violations.push({ kind: 'unsafe-path', entry: name });

    const ratio = compressedSize > 0 ? uncompressedSize / compressedSize : 0;
    if (ratio > maxRatio) maxRatio = ratio;
    if (ratio > limits.maxRatio) {
      violations.push({ kind: 'ratio-too-high', entry: name, ratio, limit: limits.maxRatio });
    }

    totalUncompressed += uncompressedSize;
    entries.push({
      name, compressedSize, uncompressedSize, method,
      localHeaderOffset, encrypted, unsafePath,
    });

    pos += 46 + nameLen + extraLen + commentLen;
  }

  if (totalUncompressed > limits.maxTotalUncompressed) {
    violations.push({
      kind: 'expansion-too-large',
      bytes: totalUncompressed, limit: limits.maxTotalUncompressed,
    });
  }

  return { entries, totalUncompressed, maxRatio, zip64, violations };
}

/**
 * An entry's bytes exactly as stored, still compressed if it was compressed.
 *
 * This is what makes part-preserving saves possible (§13, §24): repacking an
 * OOXML file by copying these bytes verbatim leaves untouched parts —
 * `vbaProject.bin`, charts, pivot caches — bit-identical, rather than
 * decompressing and recompressing them and hoping the result is equivalent.
 */
export function readRawEntry(buf: Uint8Array, entry: ZipEntry): Uint8Array | null {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const p = entry.localHeaderOffset;
  if (p + 30 > buf.length) return null;
  if (view.getUint32(p, true) !== 0x04034b50) return null;

  // The local header repeats the name and extra-field lengths, and they can
  // legitimately differ from the central directory's — the data offset must be
  // computed from the local header, never from the central one.
  const nameLen = view.getUint16(p + 26, true);
  const extraLen = view.getUint16(p + 28, true);
  const start = p + 30 + nameLen + extraLen;
  const end = start + entry.compressedSize;
  if (end > buf.length) return null;
  return buf.subarray(start, end);
}

/**
 * Read a stored (method 0) entry's bytes.
 *
 * ODF, EPUB and HWPX are required by their specifications to store the
 * `mimetype` entry first and uncompressed, which is what makes cheap container
 * identification possible without pulling in an inflate implementation.
 */
export function readStoredEntry(buf: Uint8Array, entry: ZipEntry): Uint8Array | null {
  if (entry.method !== 0) return null;
  return readRawEntry(buf, entry);
}

/** Convenience: the `mimetype` entry's value, when present and stored. */
export function readZipMimetype(buf: Uint8Array, dir: ZipDirectory): string | undefined {
  const entry = dir.entries.find((e) => e.name === 'mimetype');
  if (!entry) return undefined;
  const bytes = readStoredEntry(buf, entry);
  if (!bytes) return undefined;
  return new TextDecoder('utf-8').decode(bytes).trim();
}
